// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, GuardianTabBar, ElderTabBar, supportPhone } from "./common";
import { StatusBar, TabBar } from "./mobile-manager";

// 사용자 매뉴얼 공용 셸 + 콘텐츠 프리미티브.
// 역할별 매뉴얼(manual-region / manual-system …)이 동일한 앱 셸(Sidebar+Topbar)과
// 2단(스티키 TOC + 읽기 컬럼) 레이아웃, 본문 프리미티브를 공유한다.
// 각 매뉴얼은 role·뱃지·제목·TOC·섹션만 채운다.

// 섹션 — H2 + 앵커(scrollMarginTop으로 TOC 점프 시 상단 여백 확보).
function Section({ id, title, children }) {
  return (
    <section style={{ marginTop: 40, scrollMarginTop: 24 }}>
      <h2 id={id} style={{ fontSize: 20, fontWeight: 800, color: "var(--ink-1)", margin: "0 0 12px", scrollMarginTop: 24 }}>{title}</h2>
      <div style={{ fontSize: 14, lineHeight: 1.8, color: "var(--ink-2)" }}>{children}</div>
    </section>
  );
}

// 섹션 안 소제목/요약 — "무엇을 하나요".
function Lead({ children }) {
  return <p style={{ margin: "8px 0 14px", fontSize: 13.5, color: "var(--ink-2)" }}>{children}</p>;
}

// 번호 매긴 절차 — "어떻게 하나요".
function Steps({ items }) {
  return (
    <ol style={{ margin: "10px 0 0", paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ flex: "0 0 auto", width: 22, height: 22, borderRadius: 11, background: "var(--orange-100, #FCEBDD)", color: "var(--orange-700)", fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>{i + 1}</span>
          <span style={{ fontSize: 13.5, lineHeight: 1.65, color: "var(--ink-2)" }}>{it}</span>
        </li>
      ))}
    </ol>
  );
}

// 주의/권한 경계 박스.
function Note({ children }) {
  return (
    <div style={{ margin: "16px 0 4px", padding: "12px 14px", background: "var(--bg-sub)", border: "1px solid var(--border-soft)", borderLeft: "3px solid var(--orange-500)", borderRadius: 10, fontSize: 12.5, lineHeight: 1.7, color: "var(--ink-2)" }}>
      <span style={{ fontWeight: 800, color: "var(--orange-700)" }}>⚠ 주의 </span>
      {children}
    </div>
  );
}

// 관련 화면 바로가기 버튼들 — 드리프트 감지 테스트가 이 href들을 스크랩한다.
function GoTo({ links }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
      {links.map((l) => (
        <a key={l.href} href={l.href}
           style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 9, border: "1px solid var(--border-soft)", background: "var(--surface)", color: "var(--ink-1)", fontSize: 12.5, fontWeight: 700, textDecoration: "none" }}>
          {l.label}
          <span style={{ display: "inline-flex", color: "var(--orange-600)" }}>{Ic.arrow}</span>
        </a>
      ))}
    </div>
  );
}

// 실제 화면 스크린샷 — public/manual-shots/<role>/<key>.png 를 가리킨다.
// 이미지는 npm run manual:shots 로 재생성하며, 클릭하면 원본을 새 탭에서 연다.
// 파일 존재 여부는 드리프트 스펙 M-03 이 검증한다(누락/오타 방지).
function Shot({ src, alt, caption }) {
  return (
    <figure style={{ margin: "16px 0 4px" }}>
      <a href={src} target="_blank" rel="noreferrer" style={{ display: "block" }}>
        <img src={src} alt={alt} loading="lazy"
             style={{ width: "100%", display: "block", borderRadius: 10, border: "1px solid var(--border-soft)", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }} />
      </a>
      {caption && <figcaption style={{ marginTop: 6, fontSize: 11.5, color: "var(--ink-3)" }}>{caption}</figcaption>}
    </figure>
  );
}

// 화면 흐름 — 썸네일을 화살표로 이어 단계별 전환을 보여준다.
// steps = [{ src, label }]. 각 썸네일 클릭 시 원본 화면 이미지를 연다.
function FlowStrip({ steps }) {
  return (
    <div style={{ margin: "18px 0 4px", display: "flex", alignItems: "stretch", gap: 8, overflowX: "auto", paddingBottom: 6 }}>
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <a href={s.src} target="_blank" rel="noreferrer" style={{ flex: "0 0 auto", width: 200, textDecoration: "none", color: "inherit" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "var(--orange-700)", marginBottom: 4 }}>{`${i + 1}. ${s.label}`}</div>
            <img src={s.src} alt={s.label} loading="lazy"
                 style={{ width: 200, height: 125, objectFit: "cover", objectPosition: "top left", borderRadius: 8, border: "1px solid var(--border-soft)", display: "block" }} />
          </a>
          {i < steps.length - 1 && (
            <div style={{ flex: "0 0 auto", alignSelf: "center", color: "var(--ink-3)", display: "flex", alignItems: "center" }}>{Ic.arrow}</div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// PDF 저장 버튼 — 브라우저 인쇄 대화상자를 열어 "PDF로 저장"하게 한다.
// 클라이언트 PDF 라이브러리(jsPDF 등) 없이 네이티브 인쇄를 쓰므로 한글 폰트·
// 텍스트 선택·자동 페이지 분할이 그대로 유지된다. 인쇄용 레이아웃은 design.html.erb
// 의 @media print 규칙이 처리하며, 버튼 자신은 data-no-print 로 출력에서 제외된다.
function PdfButton({ compact = false }) {
  return (
    <button type="button" data-no-print onClick={() => window.print()}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
        padding: compact ? "5px 9px" : "7px 12px", borderRadius: compact ? 999 : 9,
        border: "1px solid var(--border-soft)", background: "var(--surface)",
        color: "var(--ink-1)", fontSize: compact ? 11 : 12.5, fontWeight: 700, whiteSpace: "nowrap" }}>
      <span style={{ width: 14, height: 14, display: "inline-flex", color: "var(--orange-600)" }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v12M8 11l4 4 4-4" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
        </svg>
      </span>
      PDF 저장
    </button>
  );
}

// 공용 셸. role=Sidebar 키, badge=가이드 라벨, toc=목차, children=섹션들.
function ManualShell({ role, topbarTitle, topbarSub, badge, title, revised, intro, toc, children }) {
  return (
    <div className="app" style={{ height: "100vh" }}>
      <div className="shell">
        <Sidebar role={role} active="manual" />
        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Topbar title={topbarTitle} sub={topbarSub} />
          <div className="main">

            <div style={{ maxWidth: 1040, margin: "0 auto", display: "grid", gridTemplateColumns: "220px 1fr", gap: 44, alignItems: "start" }}>

              {/* 스티키 목차 + 문의 */}
              <aside style={{ position: "sticky", top: 8, alignSelf: "start" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "var(--orange-700)", letterSpacing: "0.08em", marginBottom: 10 }}>{badge}</div>
                <nav style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {toc.map((t) => (
                    <a key={t.id} href={`#${t.id}`} style={{ fontSize: 12.5, color: "var(--ink-2)", textDecoration: "none", padding: "6px 10px", borderRadius: 8 }}>{t.label}</a>
                  ))}
                </nav>
                <div style={{ marginTop: 16, padding: 14, background: "var(--bg-sub)", borderRadius: 12, fontSize: 11.5, color: "var(--ink-3)", lineHeight: 1.7 }}>
                  <div style={{ fontWeight: 800, color: "var(--ink-2)", marginBottom: 4 }}>도움이 필요하신가요?</div>
                  통합돌봄 운영지원<br />
                  support@care.local<br />
                  {supportPhone()} (평일 09:00–18:00)
                </div>
              </aside>

              {/* 읽기 컬럼 */}
              <main data-manual-body style={{ maxWidth: 760, paddingBottom: 64 }}>
                <div data-no-print style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
                  <PdfButton />
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, color: "var(--orange-700)", letterSpacing: "0.08em", marginBottom: 8 }}>{badge}</div>
                <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-0.02em", margin: 0, color: "var(--ink-1)" }}>{title}</h1>
                <div style={{ fontSize: 12, marginTop: 8, color: "var(--ink-3)" }}>{revised}</div>
                <p style={{ marginTop: 20, fontSize: 14, lineHeight: 1.8, color: "var(--ink-2)" }}>{intro}</p>
                {children}
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 모바일 매뉴얼(케어매니저) ────────────────────────────────
// 데스크톱 ManualShell 대신 폰 프레임(390×844) 안에서 단일 컬럼 + 실제 하단 탭바로
// 렌더한다. 진입은 모바일 홈(/m/home) 헤더 우측 버튼. 폰 스크린샷은 세로 비율이라
// 데스크톱 Shot/FlowStrip 과 다른 ShotMobile/FlowStripMobile 을 쓴다.

// 세로(폰) 스크린샷 — 좁은 화면을 고려해 중앙 정렬 미리보기로 보여주고,
// 탭하면 원본을 새 탭에서 연다. 파일 존재는 드리프트 스펙 M-03 이 검증한다.
function ShotMobile({ src, alt, caption }) {
  return (
    <figure style={{ margin: "12px 0 4px", textAlign: "center" }}>
      <a href={src} target="_blank" rel="noreferrer" style={{ display: "inline-block" }}>
        <img src={src} alt={alt} loading="lazy"
             style={{ width: 188, maxWidth: "62%", display: "inline-block", borderRadius: 16, border: "1px solid var(--border-soft)", boxShadow: "0 2px 10px rgba(0,0,0,.10)" }} />
      </a>
      {caption && <figcaption style={{ marginTop: 6, fontSize: 11, color: "var(--ink-3)", lineHeight: 1.5 }}>{caption}</figcaption>}
    </figure>
  );
}

// 화면 흐름(세로 폰 썸네일 + 화살표) — 가로 스크롤.
function FlowStripMobile({ steps }) {
  return (
    <div style={{ margin: "14px 0 4px", display: "flex", alignItems: "stretch", gap: 6, overflowX: "auto", paddingBottom: 6 }}>
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <a href={s.src} target="_blank" rel="noreferrer" style={{ flex: "0 0 auto", width: 92, textDecoration: "none", color: "inherit" }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: "var(--orange-700)", marginBottom: 3, lineHeight: 1.25 }}>{`${i + 1}. ${s.label}`}</div>
            <img src={s.src} alt={s.label} loading="lazy"
                 style={{ width: 92, height: 168, objectFit: "cover", objectPosition: "top center", borderRadius: 12, border: "1px solid var(--border-soft)", display: "block" }} />
          </a>
          {i < steps.length - 1 && (
            <div style={{ flex: "0 0 auto", alignSelf: "center", color: "var(--ink-3)", display: "flex", alignItems: "center" }}>{Ic.arrow}</div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// 모바일 매뉴얼 셸 — StatusBar + 헤더(뒤로) + 스크롤 본문 + 실제 TabBar.
// nav 로 하단탭/홈 링크를 페르소나에 맞춰 전환한다(매니저=기본, 보호자=guardian, 어르신=elder).
// 매니저 호출부는 기본값(nav="manager", homeHref="/m/home")이라 동작 변화 없음.
function ManualMobileShell({ badge, title, revised, intro, toc, children, tab = "home", nav = "manager", homeHref = "/m/home" }) {
  return (
    <div className="app" style={{ background: "var(--bg)", overflow: "hidden" }}>
      <StatusBar />
      <div style={{ padding: "8px 18px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", flex: "0 0 auto", borderBottom: "1px solid var(--border-soft)" }}>
        <div style={{ fontSize: 17, fontWeight: 800 }}>사용자 매뉴얼</div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <PdfButton compact />
          <a href={homeHref} aria-label="홈으로" style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-3)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 16, height: 16, display: "inline-flex" }}>{Ic.close}</span>홈
          </a>
        </div>
      </div>

      <div data-manual-body style={{ flex: 1, overflow: "auto", padding: "12px 18px 24px" }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: "var(--orange-700)", letterSpacing: "0.08em", marginBottom: 6 }}>{badge}</div>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", margin: 0, color: "var(--ink-1)" }}>{title}</h1>
        <div style={{ fontSize: 11, marginTop: 6, color: "var(--ink-3)" }}>{revised}</div>
        <p style={{ marginTop: 14, fontSize: 13, lineHeight: 1.7, color: "var(--ink-2)" }}>{intro}</p>

        {/* 목차 — 좁은 화면이라 칩 형태 앵커. */}
        {toc && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "12px 0 4px" }}>
            {toc.map((t) => (
              <a key={t.id} href={`#${t.id}`} style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-2)", textDecoration: "none", padding: "5px 9px", borderRadius: 999, border: "1px solid var(--border-soft)", background: "var(--surface)" }}>{t.label}</a>
            ))}
          </div>
        )}

        {children}
      </div>

      {nav === "guardian" ? <GuardianTabBar active={tab} />
        : nav === "elder" ? <ElderTabBar active={tab} />
        : <TabBar active={tab} />}
    </div>
  );
}

export {
  ManualShell, Section, Lead, Steps, Note, GoTo, Shot, FlowStrip,
  ManualMobileShell, ShotMobile, FlowStripMobile,
};
